import { getAdminPayments } from '@/api/admin';
import PaymentsTable from '@/components/admin/payments';
import DateRangeFilter from '@/components/admin/payments/DateRangeFilter';
import RefundPaymentPanel from '@/components/admin/payments/RefundPaymentPanel';
import { BookingStatus, PaymentAdminStatus, PaymentsAdmin } from '@/types';
import { ROUTES } from '@/types/routes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Flex, message, Modal, Typography } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import { Link, useSearchParams } from 'react-router-dom';
import './index.scss';
import { downloadPayments } from 'src/api/admin';
import { useMemo, useState } from 'react';
import { startManualProcessing } from '@/api/payments';

const buildColumns = (onRefundClick: (record: PaymentsAdmin) => void) => [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    render: (text: string) => (
      <Typography.Link style={{ fontWeight: 500, color: '#1C8271' }}>
        {text}
      </Typography.Link>
    ),
  },
  {
    title: 'Franchise',
    dataIndex: 'franchise',
    key: 'franchise',
    render: (franchisor: { id: string; company: string }) => (
      <Link
        style={{ fontWeight: 500, color: '#1C8271' }}
        to={`${ROUTES.ADMIN_FRANCHISES}/${franchisor.id}`}
      >
        {franchisor.company}
      </Link>
    ),
  },
  {
    title: 'Consultant',
    dataIndex: 'consultant',
    key: 'consultant',
    render: (consultant: {
      id: string;
      firstName: string;
      lastName: string;
    }) => (
      <Link
        style={{ fontWeight: 500, color: '#1C8271' }}
        to={`${ROUTES.ADMIN_CONSULTANTS}/${consultant.id}`}
      >
        {consultant.firstName} {consultant.lastName}
      </Link>
    ),
  },
  {
    title: 'Date',
    dataIndex: 'start',
    key: 'start',
    render: (date: string) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Typography.Text>
          {date ? format(new Date(date), 'd MMM, yyyy, h:mm a') : 'N/A'}
        </Typography.Text>
      </div>
    ),
  },
  {
    title: 'Marked As Completed',
    dataIndex: 'markedAsCompletedAt',
    key: 'markedAsCompletedAt',
    render: (date: string, record: PaymentsAdmin) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Typography.Text>
          {record.status === PaymentAdminStatus.COMPLETED && date
            ? format(new Date(date), 'd MMM, yyyy, h:mm a')
            : 'N/A'}
        </Typography.Text>
      </div>
    ),
  },
  {
    title: 'Total ($)',
    dataIndex: 'paidAmount',
    key: 'paidAmount',
  },
  {
    title: 'Meeting Status',
    dataIndex: 'status',
    key: 'status',
    render: (status: 'pending' | 'completed' | 'declined') => {
      const statusColors = {
        pending: '#FFA500',
        declined: '#F68B8B',
        completed: '#1C8271',
      };

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: statusColors[status],
            }}
          />
          <Typography.Text>
            {status.slice(0, 1).toUpperCase() + status.slice(1)}
          </Typography.Text>
        </div>
      );
    },
  },
  {
    title: 'Payment Status',
    key: 'paymentStatus',
    render: (_: unknown, record: PaymentsAdmin) => {
      const { label, color } = record.isRefunded
        ? { label: 'Refunded', color: '#2A4BC2' }
        : record.isPaid
        ? { label: 'Paid', color: '#1C8271' }
        : { label: 'Unpaid', color: 'gray' };

      const canRefund =
        record.isPaid &&
        !record.isRefunded &&
        typeof record.paidAmount === 'number' &&
        record.paidAmount > 0 &&
        !!record.transactionId;

      const dot = (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: color,
          }}
        />
      );

      if (canRefund) {
        return (
          <button
            type="button"
            className="payment-status-refund-btn"
            onClick={() => onRefundClick(record)}
          >
            {dot}
            <span className="payment-status-refund-btn__label">{label}</span>
            <RightOutlined className="payment-status-refund-btn__chevron" />
          </button>
        );
      }

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {dot}
          <Typography.Text>{label}</Typography.Text>
        </div>
      );
    },
  },
  {
    title: 'Payment Date',
    dataIndex: 'paidAt',
    key: 'paidAt',
    render: (date: string | null) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Typography.Text>
          {date ? format(new Date(date), 'd MMM, yyyy, h:mm a') : 'N/A'}
        </Typography.Text>
      </div>
    ),
  },
  {
    title: 'Refund Date',
    dataIndex: 'refundedAt',
    key: 'refundedAt',
    render: (date: string | null) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Typography.Text>
          {date ? format(new Date(date), 'd MMM, yyyy, h:mm a') : 'N/A'}
        </Typography.Text>
      </div>
    ),
  },
];

export default function AdminPayments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedPayment, setSelectedPayment] = useState<PaymentsAdmin | null>(
    null,
  );
  const queryClient = useQueryClient();

  const columns = useMemo(
    () => buildColumns((record) => setSelectedPayment(record)),
    [],
  );

  const updateDateFilter = (
    startKey: string,
    endKey: string,
    from: string,
    to: string,
  ) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (from) next.set(startKey, from);
      else next.delete(startKey);
      if (to) next.set(endKey, to);
      else next.delete(endKey);
      return next;
    });
    setPage(1);
  };

  const [
    isStartManualProcessingModalOpen,
    setIsStartManualProcessingModalOpen,
  ] = useState(false);

  const filterParams = {
    search: searchParams.get('search') || '',
    meetingStartDate: searchParams.get('meetingStartDate') || '',
    meetingEndDate: searchParams.get('meetingEndDate') || '',
    paymentStartDate: searchParams.get('paymentStartDate') || '',
    paymentEndDate: searchParams.get('paymentEndDate') || '',
    refundStartDate: searchParams.get('refundStartDate') || '',
    refundEndDate: searchParams.get('refundEndDate') || '',
    completedStartDate: searchParams.get('completedStartDate') || '',
    completedEndDate: searchParams.get('completedEndDate') || '',
  };

  const { data } = useQuery<
    {
      total: number;
      page: number;
      limit: number;
      payments: PaymentsAdmin[];
    },
    Error
  >({
    queryKey: ['payment', searchParams.toString(), page, limit],
    queryFn: () =>
      getAdminPayments({
        ...filterParams,
        page,
        limit,
      }),
  });

  const { mutateAsync: downloadPaymentsMutate, isPending } = useMutation({
    mutationFn: () => downloadPayments(filterParams),
    onError: (error: any) => {
      console.log(error);
      message.error(
        error.message || 'Payments download failed. Please try again.',
      );
    },
  });

  const handleDownloadTable = async () => {
    try {
      const { file: base64Response } = await downloadPaymentsMutate();

      if (!base64Response || typeof base64Response !== 'string') {
        throw new Error('Invalid file data');
      }

      const csvContent = atob(base64Response);
      const blob = new Blob([csvContent], { type: 'text/csv' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `payments_${new Date().toISOString()}.csv`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      message.success('File downloaded successfully');
    } catch (error: any) {
      console.error(error);
      message.error(error.message || 'Failed to download file.');
    }
  };

  const {
    mutateAsync: startManualProcessingMutate,
    isPending: manualProcessingPending,
  } = useMutation({
    mutationFn: async () => {
      return await startManualProcessing();
    },
    onError: (error: any) => {
      console.error(error);
      message.error('Failed to start manual processing');
    },
  });

  const handleStartManualProcessing = async () => {
    await startManualProcessingMutate();
    setIsStartManualProcessingModalOpen(false);
    message.success('Manual processing completed successfully');
  };

  return (
    <>
      <Flex justify="space-between" align="center" gap={24}>
        <Typography.Title
          level={2}
          style={{
            margin: '0 0 24px',
          }}
        >
          Payments Management
        </Typography.Title>
        <Button
          type="primary"
          htmlType="submit"
          className="admin-button-download-table"
          onClick={() => setIsStartManualProcessingModalOpen(true)}
          disabled={isPending}
          loading={isPending}
        >
          Start Manual Processing
        </Button>
      </Flex>

      <PaymentsTable
        payments={data?.payments || []}
        total={data?.total || 0}
        columns={columns}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
        downloadData={handleDownloadTable}
        scroll={{ x: 'max-content' }}
        paginationPosition="bottom"
        customFilters={
          <>
            <DateRangeFilter
              label="Meeting Date"
              fromValue={searchParams.get('meetingStartDate') || ''}
              toValue={searchParams.get('meetingEndDate') || ''}
              onChange={(from, to) =>
                updateDateFilter('meetingStartDate', 'meetingEndDate', from, to)
              }
            />
            <DateRangeFilter
              label="Payment Date"
              fromValue={searchParams.get('paymentStartDate') || ''}
              toValue={searchParams.get('paymentEndDate') || ''}
              onChange={(from, to) =>
                updateDateFilter('paymentStartDate', 'paymentEndDate', from, to)
              }
            />
            <DateRangeFilter
              label="Refund Date"
              fromValue={searchParams.get('refundStartDate') || ''}
              toValue={searchParams.get('refundEndDate') || ''}
              onChange={(from, to) =>
                updateDateFilter('refundStartDate', 'refundEndDate', from, to)
              }
            />
            <DateRangeFilter
              label="Marked As Completed"
              fromValue={searchParams.get('completedStartDate') || ''}
              toValue={searchParams.get('completedEndDate') || ''}
              onChange={(from, to) =>
                updateDateFilter(
                  'completedStartDate',
                  'completedEndDate',
                  from,
                  to,
                )
              }
            />
          </>
        }
      />
      <Modal
        title="Start Manual Processing"
        open={isStartManualProcessingModalOpen}
        footer={null}
        centered
      >
        <div className={'modal-section-line'} style={{ marginBlock: 16 }}>
          <Typography.Text className="general-info-text">
            This will trigger processing for all pending payments from the past
            week and any missed payments. Are you sure you want to proceed?
          </Typography.Text>
        </div>
        <Flex gap={12}>
          <Button
            onClick={handleStartManualProcessing}
            type={'primary'}
            className="admin-button-download-table"
            style={{
              paddingInline: '32px',
              width: '100%',
            }}
            disabled={manualProcessingPending}
            loading={manualProcessingPending}
          >
            Start Processing
          </Button>

          <Button
            type={'default'}
            onClick={() => setIsStartManualProcessingModalOpen(false)}
            style={{
              backgroundColor: 'white',
              color: '#1C8271',
              boxShadow: 'none',
              width: '100%',
              height: '40px',
              paddingInline: '32px',
            }}
            disabled={manualProcessingPending}
            loading={manualProcessingPending}
          >
            Cancel
          </Button>
        </Flex>
      </Modal>

      <RefundPaymentPanel
        open={!!selectedPayment}
        payment={selectedPayment}
        onClose={() => setSelectedPayment(null)}
        onSuccess={async () => {
          // Prefix match covers the full key ['payment', searchParams, page, limit]
          // used by the useQuery above; refetchType: 'active' forces the currently
          // mounted list query to refetch before we close the panel.
          await queryClient.invalidateQueries({
            queryKey: ['payment'],
            refetchType: 'active',
          });
          setSelectedPayment(null);
        }}
      />
    </>
  );
}
